import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { NewClubhouse } from "../services/clubhouseAPI";
import {
  ChPostCommentsType,
  ClubhouseFeed,
  ClubhousePostDetailResponse,
} from "../services/clubhouseFeedAPI";
import { isTextMessage } from "../utils/chat";

type FeedState = {
  allChPosts: ClubhouseFeed[];
  chPosts: ClubhouseFeed[];
  pinnedChPosts: ClubhouseFeed[];
  currentClubhouse: NewClubhouse | null;
  chHandpickedPosts: ClubhouseFeed[];
  allHandpickedPosts: ClubhouseFeed[];
  allBookmarkedPosts: ClubhouseFeed[];
  chBookmarkedPosts: ClubhouseFeed[];
  chHuddles: any;
};

const togglePostUpvoteCb = (post: ClubhouseFeed, postId: string) => {
  if (post._id === postId) {
    let updatedAlreadyUpvoted = post.isUpvoted;
    let updatedAlreadyDownvoted = post.isDownvoted;
    let updatedUpvoteCount = post.upvotes;
    let updatedDownvoteCount = post.downvotes;

    if (post.isUpvoted) {
      updatedAlreadyUpvoted = false;
      updatedUpvoteCount = post.upvotes - 1;
    } else if (post.isDownvoted) {
      updatedAlreadyUpvoted = true;
      updatedUpvoteCount = post.upvotes + 1;
      updatedAlreadyDownvoted = false;
      updatedDownvoteCount = post.downvotes - 1;
    } else {
      updatedAlreadyUpvoted = true;
      updatedUpvoteCount = post.upvotes + 1;
    }
    return {
      ...post,
      upvotes: updatedUpvoteCount!,
      isUpvoted: updatedAlreadyUpvoted,
      downvotes: updatedDownvoteCount,
      isDownvoted: updatedAlreadyDownvoted,
    };
  }
  return post;
};

const togglePostDownvoteCb = (post: ClubhouseFeed, postId: string) => {
  if (post._id === postId) {
    let updatedAlreadyDownvoted = post.isDownvoted;
    let updatedAlreadyUpvoted = post.isUpvoted;
    let updatedDownvoteCount = post.downvotes;
    let updatedUpvoteCount = post.upvotes;
    if (post.isDownvoted) {
      updatedAlreadyDownvoted = false;
      updatedDownvoteCount = post.downvotes - 1;
    } else if (post.isUpvoted) {
      updatedAlreadyUpvoted = false;
      updatedUpvoteCount = post.upvotes - 1;
      updatedAlreadyDownvoted = true;
      updatedDownvoteCount = post.downvotes + 1;
    } else {
      updatedAlreadyDownvoted = true;
      updatedDownvoteCount = post.downvotes + 1;
    }
    return {
      ...post,
      upvotes: updatedUpvoteCount,
      isUpvoted: updatedAlreadyUpvoted,
      downvotes: updatedDownvoteCount,
      isDownvoted: updatedAlreadyDownvoted,
    };
  }
  return post;
};

const addCommentCb = (
  post: ClubhouseFeed,
  postId: string,
  comments: ChPostCommentsType[],
  updatedPost: ClubhouseFeed
) => {
  if (post._id === postId) {
    return updatedPost;
  }
  return post;
};

const deleteCommentCb = (post: ClubhouseFeed, postId: string) => {
  if (post._id === postId) {
    const updatedCommentCount = post.commentCount - 1;
    return {
      ...post,
      commentCount: updatedCommentCount,
    };
  }
  return post;
};

const editPostCb = (post: ClubhouseFeed, updatedPost: ClubhouseFeed) => {
  if (post._id === updatedPost?._id) {
    return updatedPost;
  }
  return post;
};

const deletePostCb = (post: ClubhouseFeed, postId: string) => {
  return post._id !== postId;
};

const toggleChPostPinnedCb = (
  post: ClubhouseFeed,
  postId: string,
  chId: string
) => {
  if (post._id === postId) {
    return {
      ...post,
      pinned: !post?.pinned,
    };
  }

  if (post?.clubhouseMeta?._id === chId) {
    return {
      ...post,
      pinned: false,
    };
  }

  return post;
};

const bookmarkPostCb = (
  post: ClubhouseFeed,
  postId: string,
  bookmarked: boolean
) => {
  if (post._id === postId) {
    return {
      ...post,
      bookmarked: bookmarked,
    };
  }
  return post;
};

const bookmarkAndRemovePostCb = (
  post: ClubhouseFeed,
  postId: string,
  bookmarked: boolean
) => {
  if (post._id === postId) {
    return {
      ...post,
      bookmarked: bookmarked,
    };
  }
  return post;
};

const superconnectCb = (post: ClubhouseFeed, authorId: string) => {
  if (post?.author?._id === authorId) {
    return {
      ...post,
      author: {
        ...post.author,
        connected: true,
      },
    };
  }
  return post;
};

const setPollVoteCb = (
  post: ClubhouseFeed,
  postId: string,
  pollOptionId: number
) => {
  if (post._id === postId) {
    const updatedPollAnswers = {
      ...post.poll?.pollAnswers,
      [pollOptionId]: post.poll?.pollAnswers[Number(pollOptionId)]! + 1 || 1,
    };
    const updatedUserVotedPollOption = pollOptionId;
    const updatedPollOptions = {
      ...post.poll,
      pollAnswers: updatedPollAnswers,
      userVotedPollOption: updatedUserVotedPollOption,
    };
    return {
      ...post,
      poll: updatedPollOptions,
    };
  }
  return post;
};

const getChPost = (posts: ClubhouseFeed[], postId: string) => {
  const newPosts = posts.filter((post) => post._id !== postId);
  const post = posts.find((post) => post._id !== postId);
  return { newPosts, post };
};

const chFeedSlice = createSlice({
  name: "chFeed",
  initialState: {
    allChPosts: [],
    chPosts: [],
    currentClubhouse: null,
    pinnedChPosts: [],
    chHandpickedPosts: [],
    allHandpickedPosts: [],
    allBookmarkedPosts: [],
    chBookmarkedPosts: [],
    chHuddles: [],
  } as FeedState,
  reducers: {
    setallChPosts: (
      state,
      {
        payload: { posts, add = false, reset = false },
      }: PayloadAction<{
        posts: ClubhouseFeed[] | ClubhouseFeed;
        add?: boolean;
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.allChPosts = posts as ClubhouseFeed[];
      } else if (add) {
        const mergedPosts = [posts, ...state.allChPosts];
        state.allChPosts = mergedPosts as ClubhouseFeed[];
      } else {
        const mergedPosts = [
          ...state.allChPosts,
          ...(posts as ClubhouseFeed[]),
        ];
        state.allChPosts = mergedPosts as ClubhouseFeed[];
      }
    },

    setAllHandpickedPosts: (
      state,
      {
        payload: { posts, add = false, reset = false },
      }: PayloadAction<{
        posts: ClubhouseFeed[] | ClubhouseFeed;
        add?: boolean;
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.allHandpickedPosts = posts as ClubhouseFeed[];
      } else if (add) {
        const mergedPosts = [posts, ...state.allHandpickedPosts];
        state.allHandpickedPosts = mergedPosts as ClubhouseFeed[];
      } else {
        const mergedPosts = [
          ...state.allHandpickedPosts,
          ...(posts as ClubhouseFeed[]),
        ];
        state.allHandpickedPosts = mergedPosts as ClubhouseFeed[];
      }
    },

    setChPosts: (
      state,
      {
        payload: { posts, add = false, reset = false },
      }: PayloadAction<{
        posts: ClubhouseFeed[] | ClubhouseFeed;
        add?: boolean;
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.chPosts = posts as ClubhouseFeed[];
      } else if (add) {
        const mergedPosts = [posts, ...state.chPosts];
        state.chPosts = mergedPosts as ClubhouseFeed[];
      } else {
        const mergedPosts = [...state.chPosts, ...(posts as ClubhouseFeed[])];
        state.chPosts = mergedPosts as ClubhouseFeed[];
      }
    },
    setChHandpickedPosts: (
      state,
      {
        payload: { posts, add = false, reset = false },
      }: PayloadAction<{
        posts: ClubhouseFeed[] | ClubhouseFeed;
        add?: boolean;
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.chHandpickedPosts = posts as ClubhouseFeed[];
      } else if (add) {
        const mergedPosts = [posts, ...state.chHandpickedPosts];
        state.chHandpickedPosts = mergedPosts as ClubhouseFeed[];
      } else {
        const mergedPosts = [
          ...state.chHandpickedPosts,
          ...(posts as ClubhouseFeed[]),
        ];
        state.chHandpickedPosts = mergedPosts as ClubhouseFeed[];
      }
    },
    setChHuddles: (
      state,
      {
        payload: { huddles, reset = false },
      }: PayloadAction<{
        huddles: ClubhouseFeed[] | ClubhouseFeed;
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.chHuddles = huddles as ClubhouseFeed[];
      } else {
        const mergedHuddles = [
          ...state.chHuddles,
          ...(huddles as ClubhouseFeed[]),
        ];
        state.chHuddles = mergedHuddles as ClubhouseFeed[];
      }
    },
    setCurrentClubhouseDetail: (
      state,
      {
        payload: { clubhouse, reset = false, name, description },
      }: PayloadAction<{
        clubhouse: NewClubhouse;
        reset?: boolean;
        name?: string;
        description?: string;
      }>
    ) => {
      if (reset) {
        state.currentClubhouse = {
          ...state.currentClubhouse!,
          name: name!,
          description: description!,
        };
      } else {
        state.currentClubhouse = clubhouse;
      }
    },
    setAllBookmarkedPosts: (
      state,
      {
        payload: { posts, add = false, reset = false },
      }: PayloadAction<{
        posts: ClubhouseFeed[] | ClubhouseFeed;
        add?: boolean;
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.allBookmarkedPosts = posts as ClubhouseFeed[];
      } else {
        const mergedPosts = [
          ...state.allBookmarkedPosts,
          ...(posts as ClubhouseFeed[]),
        ];
        state.allBookmarkedPosts = mergedPosts as ClubhouseFeed[];
      }
    },
    setChBookmarkedPosts: (
      state,
      {
        payload: { posts, add = false, reset = false },
      }: PayloadAction<{
        posts: ClubhouseFeed[] | ClubhouseFeed;
        add?: boolean;
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.chBookmarkedPosts = posts as ClubhouseFeed[];
      } else {
        const mergedPosts = [
          ...state.chBookmarkedPosts,
          ...(posts as ClubhouseFeed[]),
        ];
        state.chBookmarkedPosts = mergedPosts as ClubhouseFeed[];
      }
    },
    setPinnedChPosts: (
      state,
      {
        payload: { postId, clubhouseId },
      }: PayloadAction<{
        postId?: string;
        // clubhouses?: ClubhouseFeed[];
        clubhouseId: string;
      }>
    ) => {
      const updatedallChPosts = state.allChPosts.map((post) =>
        toggleChPostPinnedCb(post, postId!, clubhouseId)
      );
      const updatedChPosts = state.chPosts.map((post) =>
        toggleChPostPinnedCb(post, postId!, clubhouseId)
      );
      const updatedAllHandpickedPosts = state.allHandpickedPosts.map((post) =>
        toggleChPostPinnedCb(post, postId!, clubhouseId)
      );
      const updatedHandpickedPosts = state.chHandpickedPosts.map((post) =>
        toggleChPostPinnedCb(post, postId!, clubhouseId)
      );
      const updatedAllBookmarkedPosts = state.allBookmarkedPosts.map((post) =>
        toggleChPostPinnedCb(post, postId!, clubhouseId)
      );
      const updatedBookmarkedPosts = state.chBookmarkedPosts.map((post) =>
        toggleChPostPinnedCb(post, postId!, clubhouseId)
      );

      state.allChPosts = updatedallChPosts;
      state.chPosts = updatedChPosts;
      state.allHandpickedPosts = updatedAllHandpickedPosts;
      state.chHandpickedPosts = updatedHandpickedPosts;
      state.allBookmarkedPosts = updatedAllBookmarkedPosts;
      state.chBookmarkedPosts = updatedBookmarkedPosts;
    },

    setBookmarkPost: (
      state,
      {
        payload: { postId, bookmarked },
      }: PayloadAction<{ postId: string; bookmarked: boolean }>
    ) => {
      const updatedallChPosts = state.allChPosts.map((post) =>
        bookmarkPostCb(post, postId, bookmarked)
      );
      const updatedchPosts = state.chPosts.map((post) =>
        bookmarkPostCb(post, postId, bookmarked)
      );
      const updatedAllHandpickedPosts = state.allHandpickedPosts.map((post) =>
        bookmarkPostCb(post, postId, bookmarked)
      );
      const updatedChHandpickedPosts = state.chHandpickedPosts.map((post) =>
        bookmarkPostCb(post, postId, bookmarked)
      );
      const updatedallBookmarkedPosts = state.allBookmarkedPosts.filter(
        (post) => post._id !== postId
      );
      const updatedChBookmarkedPosts = state.chBookmarkedPosts.filter(
        (post) => post._id !== postId
      );
      state.allChPosts = updatedallChPosts;
      state.chPosts = updatedchPosts;
      state.allBookmarkedPosts = updatedallBookmarkedPosts;
      state.chBookmarkedPosts = updatedChBookmarkedPosts;
      state.allHandpickedPosts = updatedAllHandpickedPosts;
      state.chHandpickedPosts = updatedChHandpickedPosts;
    },
    togglePostUpvote: (
      state,
      { payload: { postId } }: PayloadAction<{ postId: string }>
    ) => {
      const updatedallChPosts = state.allChPosts.map((post) =>
        togglePostUpvoteCb(post, postId)
      );
      const updatedChPosts = state.chPosts.map((post) =>
        togglePostUpvoteCb(post, postId)
      );
      const updatedAllHandpickedPosts = state.allHandpickedPosts.map((post) =>
        togglePostUpvoteCb(post, postId)
      );
      const updatedChHandpickedPosts = state.chHandpickedPosts.map((post) =>
        togglePostUpvoteCb(post, postId)
      );
      const updatedAllBookmarkedPosts = state.allBookmarkedPosts.map((post) =>
        togglePostUpvoteCb(post, postId)
      );
      const updatedChBookmarkedPosts = state.chBookmarkedPosts.map((post) =>
        togglePostUpvoteCb(post, postId)
      );
      state.allChPosts = updatedallChPosts;
      state.chPosts = updatedChPosts;
      state.allHandpickedPosts = updatedAllHandpickedPosts;
      state.chHandpickedPosts = updatedChHandpickedPosts;
      state.allBookmarkedPosts = updatedAllBookmarkedPosts;
      state.chBookmarkedPosts = updatedChBookmarkedPosts;
    },
    togglePostDownvote: (
      state,
      { payload: { postId } }: PayloadAction<{ postId: string }>
    ) => {
      const updatedallChPosts = state.allChPosts.map((post) =>
        togglePostDownvoteCb(post, postId)
      );
      const updatedChPosts = state.chPosts.map((post) =>
        togglePostDownvoteCb(post, postId)
      );

      const updatedAllHandpickedPosts = state.allHandpickedPosts.map((post) =>
        togglePostDownvoteCb(post, postId)
      );
      const updatedChHandpickedPosts = state.chHandpickedPosts.map((post) =>
        togglePostDownvoteCb(post, postId)
      );
      const updatedAllBookmarkedPosts = state.allBookmarkedPosts.map((post) =>
        togglePostDownvoteCb(post, postId)
      );
      const updatedChBookmarkedPosts = state.chBookmarkedPosts.map((post) =>
        togglePostDownvoteCb(post, postId)
      );

      state.allChPosts = updatedallChPosts;
      state.chPosts = updatedChPosts;
      state.allHandpickedPosts = updatedAllHandpickedPosts;
      state.chHandpickedPosts = updatedChHandpickedPosts;
      state.allBookmarkedPosts = updatedAllBookmarkedPosts;
      state.chBookmarkedPosts = updatedChBookmarkedPosts;
    },
    addChPostComment: (
      state,
      {
        payload: { postId, comments, updatedPost },
      }: PayloadAction<{
        postId: string;
        comments: ChPostCommentsType[];
        updatedPost: ClubhouseFeed;
      }>
    ) => {
      const updatedallChPosts = state.allChPosts.map((post) =>
        addCommentCb(post, postId, comments, updatedPost)
      );
      const updatedchPosts = state.chPosts.map((post) =>
        addCommentCb(post, postId, comments, updatedPost)
      );
      state.allChPosts = updatedallChPosts;
      state.chPosts = updatedchPosts;
    },
    deleteChComment: (
      state,
      { payload: { postId } }: PayloadAction<{ postId: string }>
    ) => {
      const updatedallChPosts = state.allChPosts.map((post) =>
        deleteCommentCb(post, postId)
      );
      const updatedchPosts = state.chPosts.map((post) =>
        deleteCommentCb(post, postId)
      );
      state.allChPosts = updatedallChPosts;
      state.chPosts = updatedchPosts;
    },
    editChPost: (
      state,
      {
        payload: { updatedPost },
      }: PayloadAction<{ updatedPost: ClubhouseFeed }>
    ) => {
      const updatedallChPosts = state.allChPosts.map((post) =>
        editPostCb(post, updatedPost)
      );
      const updatedchPosts = state.chPosts.map((post) =>
        editPostCb(post, updatedPost)
      );
      state.allChPosts = updatedallChPosts;
      state.chPosts = updatedchPosts;
    },
    deleteChPost: (
      state,
      { payload: { postId } }: PayloadAction<{ postId: string }>
    ) => {
      const updatedallChPosts = state.allChPosts.filter((post) =>
        deletePostCb(post, postId)
      );
      const updatedchPosts = state.chPosts.filter((post) =>
        deletePostCb(post, postId)
      );
      state.allChPosts = updatedallChPosts;
      state.chPosts = updatedchPosts;
    },
    removeBookmark: (
      state,
      { payload: { postId } }: PayloadAction<{ postId: string }>
    ) => {},
    setChAuthorConnectionStatus: (
      state,
      { payload: { authorId } }: PayloadAction<{ authorId: string }>
    ) => {
      const updatedallChPosts = state.allChPosts.map((post) =>
        superconnectCb(post, authorId)
      );
      const updatedchPosts = state.chPosts.map((post) =>
        superconnectCb(post, authorId)
      );

      state.allChPosts = updatedallChPosts;
      state.chPosts = updatedchPosts;
    },
    setCreatedPostInFeed: (
      state,
      {
        payload: { post },
      }: PayloadAction<{
        post: ClubhouseFeed;
      }>
    ) => {
      if (post) {
      }
    },
    setChPollVote: (
      state,
      {
        payload: { postId, pollOptionId },
      }: PayloadAction<{ postId: string; pollOptionId: number }>
    ) => {
      const updatedallChPosts = state.allChPosts.map((post) =>
        setPollVoteCb(post, postId, pollOptionId)
      );
      const updatedchPosts = state.chPosts.map((post) =>
        setPollVoteCb(post, postId, pollOptionId)
      );

      const updatedAllHandpickedPosts = state.allHandpickedPosts.map((post) =>
        setPollVoteCb(post, postId, pollOptionId)
      );
      const updatedChHandpickedPosts = state.chHandpickedPosts.map((post) =>
        setPollVoteCb(post, postId, pollOptionId)
      );
      const updatedAllBookmarkedPosts = state.allBookmarkedPosts.map((post) =>
        setPollVoteCb(post, postId, pollOptionId)
      );
      const updatedChBookmarkedPosts = state.chBookmarkedPosts.map((post) =>
        setPollVoteCb(post, postId, pollOptionId)
      );

      state.allChPosts = updatedallChPosts;
      state.chPosts = updatedchPosts;
      state.allHandpickedPosts = updatedAllHandpickedPosts;
      state.chHandpickedPosts = updatedChHandpickedPosts;
      state.allBookmarkedPosts = updatedAllBookmarkedPosts;
      state.chBookmarkedPosts = updatedChBookmarkedPosts;
    },
  },
});

export const {
  setallChPosts,
  setAllHandpickedPosts,
  setChPosts,
  setChHandpickedPosts,
  setCurrentClubhouseDetail,
  togglePostUpvote,
  togglePostDownvote,
  deleteChPost,
  addChPostComment,
  editChPost,
  deleteChComment,
  removeBookmark,
  setChAuthorConnectionStatus,
  setCreatedPostInFeed,
  setChPollVote,
  setPinnedChPosts,
  setBookmarkPost,
  setAllBookmarkedPosts,
  setChBookmarkedPosts,
  setChHuddles,
} = chFeedSlice.actions;

export default chFeedSlice.reducer;
